// Isolated in-memory records for the meet-specific API family. No production I/O.
export const createTrainingMeetGroups = ({ getMeets, getAttendees, groupSize = 6 }) => {
    const sessions = new Map();
    let nextGroupID = 10000;
    const ensure = meetTimeID => {
        const meet = getMeets().find(row => String(row.meetTimeID) === String(meetTimeID));
        if (!meet) throw new Error('Unknown training meetTimeID');
        const key = String(meetTimeID);
        if (!sessions.has(key)) sessions.set(key, {
            meet,
            attendees: getAttendees().map(row => ({ ...structuredClone(row), scheduleID: `${key}-${row.scheduleID}`, meetGroupID: '' })),
            groups: []
        });
        return sessions.get(key);
    };
    const findGroup = groupID => [...sessions.values()].flatMap(session => session.groups).find(group => group.meetGroupID === String(groupID));
    const findSchedule = scheduleID => {
        for (const session of sessions.values()) {
            const attendee = session.attendees.find(row => row.scheduleID === String(scheduleID));
            if (attendee) return { session, attendee };
        }
        return null;
    };
    const createGroup = meetTimeID => {
        const session = ensure(meetTimeID);
        const group = {
            meetGroupID: String(nextGroupID++), bookedTime: session.meet.meetTime,
            locationID: '', locationName: '', reservationName: '', remarks: '',
            reasons: [], reasonAnalysis: '', locked: '0', modelID: '', attendees: []
        };
        session.groups.push(group);
        return group;
    };
    const assign = (scheduleID, groupID) => {
        const source = findSchedule(scheduleID);
        if (!source) throw new Error('Unknown training scheduleID');
        const target = groupID ? source.session.groups.find(group => group.meetGroupID === String(groupID)) : null;
        if (groupID && !target) throw new Error('Training target group belongs to a different meet');
        source.session.groups.forEach(group => { group.attendees = group.attendees.filter(row => row.scheduleID !== scheduleID); });
        source.attendee.meetGroupID = String(groupID || '');
        if (target) target.attendees.push(source.attendee);
    };
    return {
        reset: () => { sessions.clear(); nextGroupID = 10000; }, findGroup, findSchedule,
        ungrouped: meetTimeID => ensure(meetTimeID).attendees.filter(row => !row.meetGroupID),
        grouped: meetTimeID => ensure(meetTimeID).groups,
        create: meetTimeID => ({ meetGroupID: createGroup(meetTimeID).meetGroupID, success: 'true' }),
        assign,
        autoGroup: meetTimeID => {
            const session = ensure(meetTimeID);
            if (session.groups.some(group => String(group.locked) === '1')) throw new Error('Training groups are locked');
            session.groups = [];
            session.attendees.forEach(attendee => { attendee.meetGroupID = ''; });
            const attendees = session.attendees.filter(row => String(row.available) !== '0');
            const count = Math.ceil(attendees.length / groupSize);
            const groups = Array.from({ length: count }, () => createGroup(meetTimeID));
            attendees.forEach((attendee, index) => assign(attendee.scheduleID, groups[index % count].meetGroupID));
            return { success: 'true', groupedCount: groups.length, ungroupedCount: session.attendees.length - attendees.length };
        },
        refund: scheduleID => {
            const source = findSchedule(scheduleID);
            if (!source) throw new Error('Unknown training scheduleID');
            assign(scheduleID, '');
            source.attendee.available = '0';
        },
        move: (scheduleID, meetTimeID) => {
            const source = findSchedule(scheduleID);
            const target = ensure(meetTimeID);
            if (!source) throw new Error('Unknown training scheduleID');
            assign(scheduleID, '');
            source.session.attendees = source.session.attendees.filter(row => row.scheduleID !== scheduleID);
            target.attendees.push(source.attendee);
        }
    };
};
